import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send } from "lucide-react";
import { formatDateTimeIST } from "@/lib/formatUtils";
import logger from "@/lib/logger";

export default function EmailLogsTab() {
  const [emailLogs, setEmailLogs] = useState([]);
  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const { user } = useAuth();

  const fetchEmailLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email-logs?limit=50");
      setEmailLogs(data.entries || []);
    } catch (err) { logger.error("Failed to fetch email logs:", err); }
  }, []);

  useEffect(() => { fetchEmailLogs(); }, [fetchEmailLogs]);

  const handleSendTestEmail = async () => {
    const addr = testEmailAddr.trim() || user?.email;
    if (!addr) { toast.error("Enter email address"); return; }
    setSendingTestEmail(true);
    try {
      const { data } = await api.post("/admin/test-email", { email: addr });
      if (data.sent) toast.success(`Test email sent to ${addr}`);
      else toast.error("Email delivery failed");
      fetchEmailLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSendingTestEmail(false);
  };

  return (
    <Card className="p-4 sm:p-5 mt-4" data-testid="email-logs-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <h3 className="font-bold text-base" style={{ fontFamily: "Chivo" }}>Email Delivery Logs</h3>
        <div className="flex gap-2 items-center">
          <Input
            placeholder={user?.email || "email@example.com"}
            value={testEmailAddr}
            onChange={e => setTestEmailAddr(e.target.value)}
            className="h-9 w-48 sm:w-56 text-xs"
            data-testid="test-email-input"
          />
          <Button onClick={handleSendTestEmail} disabled={sendingTestEmail} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="send-test-email-btn">
            <Send size={14} className="mr-1" /> {sendingTestEmail ? "Sending..." : "Send Test"}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto border rounded">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Type</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">To</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Sent At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emailLogs.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-400">No email logs yet</TableCell></TableRow>
            )}
            {emailLogs.map((log, i) => (
              <TableRow key={log.id || `email-${log.sent_at}-${i}`} className="hover:bg-blue-50/50">
                <TableCell className="text-xs px-3">
                  <Badge variant="outline" className="text-[10px]">{(log.type || "").replace(/_/g, " ")}</Badge>
                </TableCell>
                <TableCell className="text-xs px-3 max-w-[200px] truncate">{Array.isArray(log.to) ? log.to.join(", ") : log.to || "—"}</TableCell>
                <TableCell className="text-xs px-3">
                  <Badge className={{"sent": "bg-emerald-100 text-emerald-700 border-emerald-200", "failed": "bg-red-100 text-red-700 border-red-200"}[log.status] || "bg-amber-100 text-amber-700 border-amber-200"}>
                    {log.status || "unknown"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs px-3 text-gray-500">{log.sent_at ? formatDateTimeIST(log.sent_at) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
